<template>
  <div class="flex flex-col p-3">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-2">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Document sync records for record search integrations.
      </p>
      <button type="button" class="btn shrink-0" :disabled="loading" @click="fetch">
        <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
        <span>Refresh</span>
      </button>
    </div>

    <div class="flex items-center gap-3 py-2 flex-wrap">
      <input
        v-model="brn"
        type="text"
        placeholder="BRN"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-44"
        @keyup.enter="search"
      />
      <input
        v-model="companyName"
        type="text"
        placeholder="Company name"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-52"
        @keyup.enter="search"
      />
      <select
        v-model="status"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="search"
      >
        <option value="PENDING">Pending</option>
        <option value="SENT">Sent</option>
        <option value="FAILED">Failed</option>
        <option value="CANCELLED">Cancelled</option>
      </select>
      <select
        v-model="perPage"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="search"
      >
        <option :value="12">12</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
      <button type="button" class="btn-sm-primary" :disabled="loading" @click="search">
        <i class="fa fa-search mr-1"></i> Search
      </button>
    </div>

    <div v-if="loading && stream.length === 0" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden mb-4">
      <div class="bg-primary h-1 rounded-full animate-pulse" style="width: 100%"></div>
    </div>

    <div class="bg-white rounded border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-xs min-w-[70rem]">
          <thead class="bg-gray-50">
          <tr>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200 w-12">#</th>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200">BRN</th>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200">BRN source</th>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200">Company name</th>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200">Tracking #</th>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200">Status</th>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">Updated</th>
            <th class="text-left py-3 px-3 font-semibold text-gray-700 border-b border-gray-200 min-w-[17rem]">Action</th>
          </tr>
          </thead>
          <tbody>
          <template v-if="loading && stream.length === 0">
            <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
              <td v-for="j in 8" :key="j" class="py-3 px-3 border-b border-gray-100">
                <div class="h-3 bg-gray-200 rounded"></div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="(row, idx) in stream" :key="rowKey(row, idx)" class="hover:bg-gray-50 transition">
              <td class="py-3 px-3 border-b border-gray-100">{{ (currentPage - 1) * perPage + idx + 1 }}</td>
              <td class="py-3 px-3 border-b border-gray-100 font-mono whitespace-nowrap">
                {{ row.brn || "—" }}
              </td>
              <td class="py-3 px-3 border-b border-gray-100">
                {{ row.brnSource || "—" }}
              </td>
              <td class="py-3 px-3 border-b border-gray-100">
                {{ row.companyName || "—" }}
              </td>
              <td class="py-3 px-3 border-b border-gray-100 font-mono whitespace-nowrap">
                {{ row.trackingNumber || "—" }}
              </td>
              <td class="py-3 px-3 border-b border-gray-100">
                <span :class="statusPill(row.status)">{{ formatStatus(row.status) }}</span>
              </td>
              <td class="py-3 px-3 border-b border-gray-100 whitespace-nowrap">{{ formatDate(row.updatedAt) }}</td>
              <td class="py-3 px-3 border-b border-gray-100">
                <div class="flex flex-wrap gap-1">
                  <button
                    type="button"
                    class="btn-sm-primary whitespace-nowrap"
                    :disabled="!resolveSyncId(row) || loadingDetailId === resolveSyncId(row)"
                    @click="viewDetails(row)"
                  >
                    <i
                      class="fa-solid mr-1"
                      :class="resolveSyncId(row) && loadingDetailId === resolveSyncId(row) ? 'fa-spinner fa-spin' : 'fa-info-circle'"
                    ></i>
                    Details
                  </button>
                  <button
                    type="button"
                    class="btn-sm-primary whitespace-nowrap"
                    :disabled="!resolveTrackingNumber(row) || resendingRowId === resolveSyncId(row)"
                    @click="resendCopies(row)"
                  >
                    <i
                      class="fa-solid mr-1"
                      :class="resolveTrackingNumber(row) && resendingRowId === resolveSyncId(row) ? 'fa-spinner fa-spin' : 'fa-paper-plane'"
                    ></i>
                    Resend
                  </button>
                  <button
                    type="button"
                    class="btn-sm-danger whitespace-nowrap"
                    :disabled="!resolveSyncId(row) || closingSyncId === resolveSyncId(row)"
                    @click="confirmClose(row)"
                  >
                    <i
                      class="fa-solid mr-1"
                      :class="resolveSyncId(row) && closingSyncId === resolveSyncId(row) ? 'fa-spinner fa-spin' : 'fa-xmark'"
                    ></i>
                    Close
                  </button>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="!loading && stream.length === 0">
            <td colspan="8" class="py-8 px-4 text-center text-gray-400 text-sm">No results found</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-if="pagination.totalPages > 1"
      class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2"
    >
      <p class="text-xs text-gray-500">
        Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
        <span class="font-semibold text-gray-700">{{ pagination.totalPages }}</span>
        <span class="text-gray-400 ml-2">({{ pagination.totalElements }} total)</span>
      </p>
      <div class="flex gap-2">
        <button class="btn-sm-primary" :disabled="currentPage === 1 || loading" @click="previous">
          <i class="fa fa-chevron-left mr-1"></i> Prev
        </button>
        <button class="btn-sm-primary" :disabled="currentPage === pagination.totalPages || loading" @click="next">
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
    </div>

    <AppModal v-model="showDetail" title="Document Sync Details" size="2xl" hide-footer>
      <div v-if="detailLoading" class="py-8 text-center text-gray-500">
        <i class="fa-solid fa-spinner fa-spin text-2xl mb-2"></i>
        <p class="text-sm">Loading details...</p>
      </div>
      <div v-else-if="selectedRow" class="space-y-4 text-sm">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Sync ID</p>
            <p class="font-mono text-xs break-all">{{ selectedRow.id || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Certification ID</p>
            <p class="font-mono text-xs break-all">{{ selectedRow.certificationId || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Tracking number</p>
            <p class="font-mono text-xs break-all">{{ selectedRow.trackingNumber || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">BRN</p>
            <p class="font-mono text-xs break-all">{{ selectedRow.brn || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">BRN source</p>
            <p class="break-words">{{ selectedRow.brnSource || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Company name</p>
            <p class="break-words">{{ selectedRow.companyName || "—" }}</p>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Status</p>
            <span :class="statusPill(selectedRow.status)">{{ formatStatus(selectedRow.status) }}</span>
          </div>
          <div class="min-w-0">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Updated</p>
            <p>{{ formatDate(selectedRow.updatedAt) }}</p>
          </div>
        </div>

        <div>
          <p class="font-bold mb-2">Items</p>
          <div v-if="detailItems.length > 0" class="overflow-x-auto border border-gray-200 rounded">
            <table class="w-full text-xs min-w-[40rem]">
              <thead class="bg-gray-50">
              <tr>
                <th class="text-left py-2 px-3 font-semibold text-gray-700 whitespace-nowrap">#</th>
                <th class="text-left py-2 px-3 font-semibold text-gray-700 min-w-[10rem]">Document name</th>
                <th class="text-left py-2 px-3 font-semibold text-gray-700 whitespace-nowrap">EDMS ID</th>
                <th class="text-left py-2 px-3 font-semibold text-gray-700 whitespace-nowrap">Status</th>
                <th class="text-left py-2 px-3 font-semibold text-gray-700 whitespace-nowrap">Updated</th>
                <th class="text-center py-2 px-3 font-semibold text-gray-700 whitespace-nowrap w-28">Retry</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, idx) in detailItems" :key="itemRowKey(item, idx)" class="border-t border-gray-100">
                <td class="py-2 px-3 align-top">{{ idx + 1 }}</td>
                <td class="py-2 px-3 align-top break-words">{{ item.documentName || "—" }}</td>
                <td class="py-2 px-3 align-top font-mono break-all">{{ item.documentEdmsId || "—" }}</td>
                <td class="py-2 px-3 align-top whitespace-nowrap">
                  <span :class="statusPill(item.status)">{{ formatStatus(item.status) }}</span>
                </td>
                <td class="py-2 px-3 align-top whitespace-nowrap">{{ formatDate(item.updatedAt) }}</td>
                <td class="py-2 px-3 align-top text-center">
                  <button
                    type="button"
                    class="btn-sm-primary whitespace-nowrap"
                    :disabled="!resolveItemId(item) || downloadingItemId === resolveItemId(item)"
                    @click="downloadItem(item)"
                  >
                    <i
                      class="fa-solid mr-1"
                      :class="downloadingItemId === resolveItemId(item) ? 'fa-spinner fa-spin' : 'fa-rotate-right'"
                    ></i>
                    Retry
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-gray-400 text-xs">No items found for this sync.</p>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { dateFormat } from "@/commons/composables/utilities.ts";
import { useConfirmDialog, useToast } from "@/composables";
import {
  closeDocumentSyncItem,
  downloadDocumentSyncItem,
  extractErrorMessage,
  fetchDocumentSyncById,
  fetchDocumentSyncRecords,
  resendDocumentSyncCopies,
  resolveItemId,
  resolveSyncId,
  resolveTrackingNumber,
  type RecordSearchDocumentSync,
  type RecordSearchDocumentSyncDetail,
  type RecordSearchSyncItem,
} from "./recordSearchApi";

const { successToast, errorToast } = useToast();
const { showConfirm } = useConfirmDialog();

const loading = ref(false);
const currentPage = ref(1);
const perPage = ref(20);
const status = ref("PENDING");
const brn = ref("");
const companyName = ref("");
const stream = ref<RecordSearchDocumentSync[]>([]);
const pagination = reactive({ totalElements: 0, totalPages: 0 });
const showDetail = ref(false);
const detailLoading = ref(false);
const loadingDetailId = ref<string | null>(null);
const resendingRowId = ref<string | null>(null);
const closingSyncId = ref<string | null>(null);
const downloadingItemId = ref<string | null>(null);
const selectedRow = ref<RecordSearchDocumentSyncDetail | null>(null);

const detailItems = computed(() => selectedRow.value?.items ?? []);

const statusPill = (value: string | null) => {
  const base = "text-xs px-2 py-0.5 rounded-full capitalize ";
  const normalized = (value || "").toUpperCase();
  if (normalized === "SUCCESS" || normalized === "SENT") return base + "bg-green-50 text-green-700";
  if (normalized === "FAILED" || normalized === "EXPIRED") return base + "bg-red-50 text-red-700";
  if (normalized === "CANCELLED") return base + "bg-orange-50 text-orange-700";
  if (normalized === "IN_PROGRESS") return base + "bg-blue-50 text-blue-700";
  if (normalized === "PENDING") return base + "bg-gray-100 text-gray-600";
  return base + "bg-gray-100 text-gray-600";
};

const formatStatus = (value: string | null) => {
  if (!value) return "—";
  return value.replace(/_/g, " ").toLowerCase();
};

const formatDate = (value: string | null) => {
  if (!value) return "—";
  return dateFormat(value) || value;
};

const rowKey = (row: RecordSearchDocumentSync, idx: number) => {
  if (row.id) return row.id;
  return `record-search-${idx}`;
};

const itemRowKey = (item: RecordSearchSyncItem, idx: number) => {
  const itemId = resolveItemId(item);
  if (itemId) return itemId;
  return `sync-item-${idx}`;
};

const downloadItem = async (item: RecordSearchSyncItem) => {
  const itemId = resolveItemId(item);
  if (!itemId) {
    errorToast("Error", "Item ID is missing for this document");
    return;
  }

  downloadingItemId.value = itemId;
  try {
    await downloadDocumentSyncItem(itemId, item.documentName);
    successToast("Success", "Retry started");
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not retry document"));
  } finally {
    downloadingItemId.value = null;
  }
};

const fetch = async () => {
  loading.value = true;
  try {
    const result = await fetchDocumentSyncRecords({
      page: currentPage.value,
      perPage: perPage.value,
      status: status.value,
      brn: brn.value,
      companyName: companyName.value,
    });
    stream.value = result.content;
    pagination.totalElements = result.totalElements;
    pagination.totalPages = result.totalPages;
  } catch (error: unknown) {
    stream.value = [];
    pagination.totalElements = 0;
    pagination.totalPages = 0;
    errorToast("Error", extractErrorMessage(error, "Could not load document sync records"));
  } finally {
    loading.value = false;
  }
};

const search = () => {
  currentPage.value = 1;
  fetch();
};

const viewDetails = async (row: RecordSearchDocumentSync) => {
  const syncId = resolveSyncId(row);
  if (!syncId) {
    errorToast("Error", "Sync ID is missing for this record");
    return;
  }

  showDetail.value = true;
  selectedRow.value = null;
  detailLoading.value = true;
  loadingDetailId.value = syncId;

  try {
    selectedRow.value = await fetchDocumentSyncById(syncId);
  } catch (error: unknown) {
    showDetail.value = false;
    errorToast("Error", extractErrorMessage(error, "Could not load sync details"));
  } finally {
    detailLoading.value = false;
    loadingDetailId.value = null;
  }
};

const resendCopies = async (row: RecordSearchDocumentSync) => {
  const syncId = resolveSyncId(row);
  const trackingNumber = resolveTrackingNumber(row);
  if (!syncId) {
    errorToast("Error", "Sync ID is missing for this record");
    return;
  }
  if (!trackingNumber) {
    errorToast("Error", "Tracking number is missing for this record");
    return;
  }

  resendingRowId.value = syncId;
  try {
    await resendDocumentSyncCopies(trackingNumber);
    successToast("Success", "Copies resend request submitted");
    fetch();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not resend copies"));
  } finally {
    resendingRowId.value = null;
  }
};

const confirmClose = async (row: RecordSearchDocumentSync) => {
  const syncId = resolveSyncId(row);
  if (!syncId) {
    errorToast("Error", "Sync ID is missing for this record");
    return;
  }

  const label = row.trackingNumber || row.companyName || row.brn || row.id || "this record";
  const confirmed = await showConfirm(
    `Close this document sync record (${label})? This action is irreversible.`,
    {
      title: "Close Document Sync",
      variant: "danger",
      okTitle: "Close",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  closingSyncId.value = syncId;
  try {
    await closeDocumentSyncItem(syncId);
    successToast("Success", "Document sync closed");
    fetch();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not close document sync"));
  } finally {
    closingSyncId.value = null;
  }
};

const next = () => {
  if (currentPage.value < pagination.totalPages) {
    currentPage.value++;
    fetch();
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetch();
  }
};

onMounted(() => fetch());
</script>
